// Icons
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ConstructionIcon from "@mui/icons-material/Construction";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import GridOn from "@mui/icons-material/GridOn";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import GroupsIcon from "@mui/icons-material/Groups";
import LockIcon from "@mui/icons-material/Lock";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import MarginIcon from "@mui/icons-material/Margin";

// Images
import scpLogo from "~/images/scp-logo.svg";
import sLogo from "~/images/s.svg";

// Lib

import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import classnames from "classnames";
import { useNavigate, useLocation } from "react-router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useUserStore, useUploadClaimProcessMapper } from "~/stores";

import { Box, Grid, Paper, ClickAwayListener } from "@mui/material";

// Hooks
import { useFlowGuard } from "~/hooks/useFlowGuard";
import { useLandingPage } from "~/hooks/useLandingPage";

// Stores

// Styles
import style from "./style.module.scss";

// Utils
import { constantMapper, truncateString } from "~/utils/string";

// Constants
import { ROWRENDERERCONST } from "~/constants/rowRenderers";

interface User {
    info: { email: string; firstName: string; lastName: string };
    roles: string[];
}
interface Props {
    user: User;
    id: string;
}

const Sidebar: React.FC<Props> = ({ user, id }) => {
    /**
     * Hooks
     */
    const navigate = useNavigate();
    const location = useLocation();
    useFlowGuard({ canResetFlow: true });
    /**
     * States
     */
    // Team
    const { team } = useUserStore();

    // Sublist
    const [sublist, setSublist] = useState("");

    const initialSelectedMenu = useMemo(() => {
        if (!location?.pathname) return "";

        const path = location?.pathname.toLowerCase();

        const items = [
            {
                key: "DASHBOARD",
                values: [
                    "/dashboard",
                    "/contract",
                    "/claim",
                    "/order",
                    "/outcome",
                    "/finance",
                    "/financial-condition",
                    "/partner",
                    "/product",
                    "/region",
                    "/create-contract/",
                ],
            },
            { key: "MAINTENANCE", values: ["/maintenance"] },
            { key: "TASKS", values: ["/tasks"] },
            { key: "REPORTS", values: ["/reports"] },
            { key: "PROCESS", values: ["/process"] },
        ];

        // Check if path starts with any of the item values
        for (const item of items) {
            for (const value of item.values) {
                const itemValue = value.toLowerCase();

                if (path.startsWith(itemValue)) {
                    return item.key;
                }
            }
        }

        // Fallback to includes check
        for (const item of items) {
            for (const value of item.values) {
                const itemValue = value.toLowerCase();
                if (path.includes(itemValue)) {
                    return item.key;
                }
            }
        }

        return "";
    }, [location?.pathname]);

    const [selectedMenu, setSelectedMenu] = useState("");

    useEffect(() => {
        if (!initialSelectedMenu) return;

        setSelectedMenu(initialSelectedMenu);
    }, [initialSelectedMenu]);

    /**
     * Permissions
     */
    const {
        isUserWithRights,
        areContractsVisible,
        areOrdersVisible,
        areOutcomesVisible,
        areFinancesVisible,
        hasPermissionToEditDataTables,
        hasPermissionToEditMaintenance,
        areTasksVisible,
        areClaimsVisible,
    } = useLandingPage();

    /**
     * States
     */
    // Reset process flow
    const { resetUploadClaimData } = useUploadClaimProcessMapper();

    /**
     * Set sidebar state in localStorage
     */
    const isOpen = useMemo(() => {
        if (typeof window !== "undefined") {
            const state = localStorage.getItem("SIDEBAR_OPEN");

            if (!state || state === "false") {
                localStorage.setItem("SIDEBAR_OPEN", "true");
                return true;
            }

            return true;
        }
    }, []);

    const [open, setOpen] = useState(isOpen);

    /**
     * Reset creation flow
     */
    const handleNavigation = (
        hasSublist: boolean,
        key: string,
        path?: string,
    ) => {
        setSelectedMenu(key);

        if (!hasSublist && path) {
            if (sublist) {
                setSublist("");
            }
            location?.pathname.includes("process") && resetUploadClaimData();
            // only clear custom items
            for (const key in sessionStorage) {
                if (/^FILTER_|^SEARCH_VALUE/.test(key)) {
                    sessionStorage.removeItem(key);
                }
            }
            navigate(path);
        } else {
            setSublist(key);
            setOpen(false);
        }
    };

    const isSublistActive = useCallback(
        (key: string, subpages?: string[]) => {
            if (subpages) {
                return (
                    subpages
                        .map(s => (s.endsWith("/") ? s.slice(0, -1) : s))
                        .includes(
                            location.pathname.endsWith("/")
                                ? location.pathname.slice(0, -1)
                                : location.pathname,
                        ) || location.pathname.includes(key)
                );
            }
            return location.pathname.includes(key);
        },
        [location],
    );

    const sublistMapper = useMemo(() => {
        return {
            DASHBOARD: {
                icon: <GridOn color="primary" />,
                items: [
                    {
                        title: "Claims",
                        path: "/dashboard/claims/",
                        disabled: !areClaimsVisible,
                    },
                    {
                        title: "Contracts",
                        path: "/dashboard/contracts/",
                        disabled: !areContractsVisible,
                    },
                    {
                        title: "Finances",
                        path: "/dashboard/finances/bpf/",
                        disabled: !areFinancesVisible,
                        subpages: [
                            "/dashboard/finances/credit-note-outcome/",
                            "/dashboard/finances/invoice-release",
                            "/dashboard/finances/credit-note-claim/",
                        ],
                    },
                    {
                        title: "Financial conditions",
                        path: "/dashboard/financial-conditions/rebate-at-infusion/",
                        disabled: !areOutcomesVisible,
                    },
                    {
                        title: "Orders",
                        path: "/dashboard/orders/",
                        disabled: !areOrdersVisible,
                    },
                    {
                        title: "Outcomes",
                        path: "/dashboard/outcomes/",
                        disabled: !areOutcomesVisible,
                    },

                    {
                        title: "Partners",
                        path: "/dashboard/partners/",
                    },

                    {
                        title: "Products",
                        path: "/dashboard/products/",
                    },

                    {
                        title: "Regions",
                        path: "/dashboard/regions/",
                    },
                ],
            },
            MAINTENANCE: {
                icon: <ConstructionIcon color="primary" />,
                items: [
                    {
                        title: "Billing plan",
                        path: "/maintenance/billing-plan/",
                    },
                    {
                        title: "Category",
                        path: "/maintenance/category",
                    },
                    {
                        title: "Context",
                        path: "/maintenance/context",
                    },
                    {
                        title: "Contract Milestones-DMN",
                        path: "/maintenance/contract-milestones-dmn/",
                    },
                    {
                        title: "Country info",
                        path: "/maintenance/country/",
                    },
                    {
                        title: "Financial conditions",
                        path: "/maintenance/financial-conditions/",
                    },
                    {
                        title: "Interface",
                        path: "/maintenance/interface/",
                    },
                    {
                        title: "Milestone",
                        path: "/maintenance/milestone/",
                    },
                    {
                        title: "Model",
                        path: "/maintenance/model",
                    },
                    {
                        title: "Partner",
                        path: "/maintenance/partner/",
                    },
                    {
                        title: "Product",
                        path: "/maintenance/product/",
                    },

                    {
                        title: "Product conditions",
                        path: "/maintenance/product-conditions/",
                    },

                    {
                        title: "System availability",
                        path: "/maintenance/system-maintenance/",
                    },
                    {
                        title: "Teams",
                        path: "/maintenance/teams/",
                        subpages: ["/maintenance/roles"],
                    },
                    {
                        title: "Users",
                        path: "/maintenance/users/",
                    },
                ],
            },
            TASKS: {
                icon: <MarginIcon color="primary" />,
                items: [
                    {
                        title: "Ongoing tasks",
                        path: "/tasks/ongoing-tasks/",
                    },
                    {
                        title: "Completed tasks",
                        path: "/tasks/completed-tasks/",
                    },
                ],
            },
            REPORTS: {
                icon: <SaveAltIcon color="primary" />,
                path: "/reports/",
            },
            ACCOUNT: {
                icon: <PersonOutlineIcon color="primary" />,
                path: "/account/",
            },
            TEAM: {
                icon: <GroupsIcon color="primary" />,
                path: "/select-team",
            },
        };
    }, [
        areContractsVisible,
        areOrdersVisible,
        areOutcomesVisible,
        areClaimsVisible,
        areFinancesVisible,
    ]);

    const isMainListActive = useCallback(
        (key: string) => {
            const mapper = sublistMapper as Record<string, any>;
            if (!mapper[key]) return false;
            if (!!mapper[key].path) {
                return location.pathname.includes(mapper[key].path);
            }
            const hasDirectPath = mapper[key]!.items.some((item: any) =>
                location.pathname.includes(item.path),
            );
            const hasSubpagePath = mapper[key]!.items.some((item: any) =>
                item.subpages
                    ? item.subpages.some((subpage: string) =>
                          location.pathname.includes(subpage),
                      )
                    : false,
            );

            if (hasDirectPath || hasSubpagePath) return true;
        },
        [location, sublistMapper],
    );

    /**
     * Render
     */
    return (
        <ClickAwayListener
            onClickAway={() => {
                setOpen(false);
                setSublist("");
            }}
        >
            <div id={`${id}-drawer`} className={style.wrapper}>
                <Drawer
                    id={`${id}-drawer`}
                    variant="persistent"
                    open={true}
                    className={classnames(style.drawer, {
                        [style.folded]: !open,
                    })}
                    classes={{
                        paper: classnames(style.paper, {
                            [style.folded]: !open,
                        }),
                    }}
                >
                    <Grid
                        container
                        size={{ xs: 12 }}
                        direction={"column"}
                        wrap="nowrap"
                        sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <Grid size={{ xs: 1 }} sx={{ flexShrink: 0 }}>
                            <Button
                                id={`${id}-logo`}
                                className={style.logo}
                                variant="text"
                                color="primary"
                                data-testid="sidebar-logo"
                                onClick={() => {
                                    navigate("/");
                                }}
                                size="small"
                            >
                                <div
                                    style={{
                                        display: open ? "block" : "none",
                                        minHeight: "3rem",
                                    }}
                                >
                                    <img
                                        src={scpLogo}
                                        alt="Smart contracting platform"
                                        style={{
                                            objectFit: "contain",
                                            width: "100%",
                                            height: "auto",
                                            maxHeight: "4rem",
                                        }}
                                    />
                                </div>
                                <div
                                    style={{ display: open ? "none" : "block" }}
                                >
                                    <img
                                        src={sLogo}
                                        alt="Smart contracting platform icon"
                                        style={{
                                            objectFit: "contain",
                                            width: "100%",
                                            height: "auto",
                                            maxHeight: "2.5rem",
                                        }}
                                    />
                                </div>
                            </Button>
                        </Grid>

                        <Grid
                            container
                            size={{ xs: 12 }}
                            direction={"column"}
                            className={style.menu}
                            wrap="nowrap"
                            sx={{
                                flexGrow: 1,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                            }}
                        >
                            <Grid
                                container
                                size={{ xs: 12 }}
                                sx={{ flexGrow: 1 }}
                            >
                                <List className={style.navs}>
                                    <ListItem
                                        disablePadding
                                        sx={{ display: "block" }}
                                        id={`${id}-list-item-opener`}
                                    >
                                        <ListItemButton
                                            id={`${id}-slider-opener-btn`}
                                            onClick={() => {
                                                setOpen(!open);
                                                if (setSublist) setSublist("");

                                                localStorage.setItem(
                                                    "SIDEBAR_OPEN",
                                                    `${!open}`,
                                                );
                                            }}
                                        >
                                            <ListItemIcon>
                                                {open ? (
                                                    <ChevronLeftIcon color="primary" />
                                                ) : (
                                                    <ChevronRightIcon color="primary" />
                                                )}
                                            </ListItemIcon>
                                        </ListItemButton>
                                    </ListItem>

                                    {!!team &&
                                        (!!areContractsVisible ||
                                            !!areOrdersVisible ||
                                            !!areOutcomesVisible ||
                                            !!areFinancesVisible ||
                                            !!hasPermissionToEditDataTables) && (
                                            <ListItem
                                                id={`${id}-dashboard`}
                                                disablePadding
                                                sx={{ display: "block" }}
                                                className={classnames({
                                                    [style.active]:
                                                        isMainListActive(
                                                            "DASHBOARD",
                                                        ),
                                                })}
                                            >
                                                <ListItemButton
                                                    id={`${id}-dashboard-btn`}
                                                    data-testid="sidebar-dashboard"
                                                    onClick={() =>
                                                        handleNavigation(
                                                            true,
                                                            "DASHBOARD",
                                                        )
                                                    }
                                                >
                                                    <ListItemIcon
                                                        className={
                                                            style.listIcon
                                                        }
                                                        sx={{
                                                            position:
                                                                "relative",
                                                        }}
                                                    >
                                                        <GridOn color="primary" />
                                                    </ListItemIcon>
                                                    <ListItemText>
                                                        <Typography variant="body2">
                                                            Dashboard
                                                        </Typography>
                                                    </ListItemText>
                                                </ListItemButton>
                                            </ListItem>
                                        )}

                                    {!!team && !!areTasksVisible && (
                                        <ListItem
                                            id={`${id}-tasks`}
                                            disablePadding
                                            sx={{ display: "block" }}
                                            className={classnames({
                                                [style.active]:
                                                    isMainListActive("TASKS"),
                                            })}
                                        >
                                            <ListItemButton
                                                id={`${id}-tasks-btn`}
                                                data-testid="sidebar-tasks"
                                                onClick={() =>
                                                    handleNavigation(
                                                        true,
                                                        "TASKS",
                                                    )
                                                }
                                            >
                                                <ListItemIcon
                                                    className={style.listIcon}
                                                    sx={{
                                                        position: "relative",
                                                    }}
                                                >
                                                    <MarginIcon color="primary" />
                                                </ListItemIcon>
                                                <ListItemText>
                                                    <Typography variant="body2">
                                                        Tasks
                                                    </Typography>
                                                </ListItemText>
                                            </ListItemButton>
                                        </ListItem>
                                    )}
                                    {!!team && !!isUserWithRights && (
                                        <ListItem
                                            id={`${id}-reports`}
                                            data-testid="sidebar-reports"
                                            disablePadding
                                            sx={{ display: "block" }}
                                            className={classnames({
                                                [style.active]:
                                                    isMainListActive("REPORTS"),
                                            })}
                                        >
                                            <ListItemButton
                                                id={`${id}-reports-btn`}
                                                onClick={() =>
                                                    handleNavigation(
                                                        false,
                                                        "REPORTS",
                                                        "/reports/",
                                                    )
                                                }
                                            >
                                                <ListItemIcon
                                                    className={style.listIcon}
                                                    sx={{
                                                        position: "relative",
                                                    }}
                                                >
                                                    <SaveAltIcon color="primary" />
                                                </ListItemIcon>
                                                <ListItemText>
                                                    <Typography variant="body2">
                                                        Reports
                                                    </Typography>
                                                </ListItemText>
                                            </ListItemButton>
                                        </ListItem>
                                    )}

                                    {hasPermissionToEditMaintenance && (
                                        <ListItem
                                            id={`${id}-maintenance`}
                                            disablePadding
                                            sx={{ display: "block" }}
                                            data-testid="sidebar-maintenance"
                                            className={classnames({
                                                [style.active]:
                                                    isMainListActive(
                                                        "MAINTENANCE",
                                                    ),
                                            })}
                                        >
                                            <ListItemButton
                                                id={`${id}-maintenance-btn`}
                                                disabled={
                                                    !hasPermissionToEditMaintenance
                                                }
                                                onClick={() =>
                                                    handleNavigation(
                                                        true,
                                                        "MAINTENANCE",
                                                    )
                                                }
                                            >
                                                <ListItemIcon
                                                    className={style.listIcon}
                                                    sx={{
                                                        position: "relative",
                                                    }}
                                                >
                                                    <ConstructionIcon color="primary" />
                                                </ListItemIcon>
                                                <ListItemText>
                                                    <Typography variant="body2">
                                                        Maintenance
                                                    </Typography>
                                                </ListItemText>
                                            </ListItemButton>
                                        </ListItem>
                                    )}
                                </List>
                            </Grid>
                            {!!user && (
                                <Grid
                                    size={{ xs: 12 }}
                                    container
                                    alignItems={"flex-end"}
                                    sx={{ flexShrink: 0 }}
                                >
                                    <List className={style.navs}>
                                        <ListItem
                                            id={`${id}-user-info`}
                                            disablePadding
                                            sx={{ display: "block" }}
                                            className={classnames({
                                                [style.active]:
                                                    isMainListActive("ACCOUNT"),
                                            })}
                                        >
                                            <Tooltip
                                                arrow
                                                title={
                                                    user?.info?.firstName &&
                                                    user?.info?.lastName
                                                        ? `${user?.info?.firstName} ${user?.info?.lastName}`
                                                        : user?.info?.email
                                                }
                                                id={`${id}-user-info-tooltip`}
                                                placement="right"
                                            >
                                                <ListItemButton
                                                    id={`${id}-user-info-btn`}
                                                    onClick={() =>
                                                        handleNavigation(
                                                            false,
                                                            "ACCOUNT",
                                                            `/account/`,
                                                        )
                                                    }
                                                    disabled={!team}
                                                >
                                                    <ListItemIcon
                                                        className={
                                                            style.listIcon
                                                        }
                                                        sx={{
                                                            position:
                                                                "relative",
                                                        }}
                                                    >
                                                        {!team && (
                                                            <LockIcon
                                                                sx={{
                                                                    fontSize:
                                                                        "0.9rem",
                                                                    position:
                                                                        "absolute",
                                                                    left: "-14px",
                                                                    top: "5px",
                                                                }}
                                                            />
                                                        )}
                                                        <PersonOutlineIcon color="primary" />
                                                    </ListItemIcon>
                                                    <ListItemText>
                                                        <Typography variant="body2">
                                                            {user?.info
                                                                ?.firstName &&
                                                            user?.info?.lastName
                                                                ? truncateString(
                                                                      `${user?.info?.firstName} ${user?.info?.lastName}`,
                                                                      12,
                                                                      undefined,
                                                                      20,
                                                                  )
                                                                : user?.info
                                                                      ?.email}
                                                        </Typography>
                                                    </ListItemText>
                                                </ListItemButton>
                                            </Tooltip>
                                        </ListItem>

                                        <ListItem
                                            id={`${id}-user-info`}
                                            data-testid="sidebar-user-info"
                                            disablePadding
                                            sx={{ display: "block" }}
                                            className={classnames({
                                                [style.active]:
                                                    isMainListActive("TEAM"),
                                            })}
                                        >
                                            <Tooltip
                                                arrow
                                                title={
                                                    team
                                                        ? team?.teamName
                                                        : "No team Selected"
                                                }
                                                id={`${id}-selected-team-tooltip`}
                                                placement="right"
                                            >
                                                <ListItemButton
                                                    id={`${id}-select-team-btn`}
                                                    onClick={() =>
                                                        handleNavigation(
                                                            false,
                                                            "TEAM",
                                                            "/select-team",
                                                        )
                                                    }
                                                >
                                                    <ListItemIcon
                                                        className={
                                                            style.listIcon
                                                        }
                                                    >
                                                        <GroupsIcon color="primary" />
                                                    </ListItemIcon>

                                                    <ListItemText>
                                                        <Typography variant="body2">
                                                            {team
                                                                ? truncateString(
                                                                      team?.teamName,
                                                                      12,
                                                                      undefined,
                                                                      19,
                                                                  )
                                                                : "No team Selected"}
                                                        </Typography>
                                                    </ListItemText>
                                                </ListItemButton>
                                            </Tooltip>
                                        </ListItem>

                                        <ListItem
                                            disablePadding
                                            sx={{ display: "block" }}
                                            id={`${id}-logout`}
                                        >
                                            <ListItemButton
                                                id={`${id}-logout-btn`}
                                                data-testid="sidebar-logout"
                                                onClick={() => {
                                                    // clear saved viewing options in local storage
                                                    Object.keys(
                                                        ROWRENDERERCONST,
                                                    ).forEach((key: string) => {
                                                        localStorage.removeItem(
                                                            key,
                                                        );
                                                    });

                                                    navigate("/logout");
                                                }}
                                            >
                                                <ListItemIcon
                                                    className={style.listIcon}
                                                >
                                                    <ExitToAppIcon color="primary" />
                                                </ListItemIcon>
                                                <ListItemText>
                                                    <Typography variant="body2">
                                                        Logout
                                                    </Typography>
                                                </ListItemText>
                                            </ListItemButton>
                                        </ListItem>
                                    </List>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Drawer>

                <div
                    id={`${id}-sublist-drawer`}
                    className={classnames(style.sublist, {
                        [style.openSublist]: !!sublist,
                    })}
                >
                    <Paper className={style.sublistPaper}>
                        <Button
                            id={`${id}-logo`}
                            startIcon={<ChevronLeftIcon color="primary" />}
                            className={style.logo}
                            variant="text"
                            color="primary"
                            onClick={() => {
                                setSublist("");
                                setOpen(true);
                            }}
                            size="small"
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                height={"1.5rem"}
                            >
                                {
                                    sublistMapper[
                                        sublist as keyof typeof sublistMapper
                                    ]?.icon
                                }
                                <span className={style.sublistHeader}>
                                    {constantMapper(sublist)}
                                </span>
                            </Box>
                        </Button>

                        <List className={style.navs}>
                            {(
                                sublistMapper[
                                    sublist as keyof typeof sublistMapper
                                ] as any
                            )?.items?.map(
                                (item: any, index: number) =>
                                    !(item as any)?.disabled && (
                                        <ListItem
                                            key={index}
                                            disablePadding
                                            sx={{ display: "block" }}
                                            id={`${id}-sublist-item-opener`}
                                            className={classnames({
                                                [style.active]: isSublistActive(
                                                    item.path,
                                                    (item as any).subpages,
                                                ),
                                            })}
                                        >
                                            <ListItemButton
                                                id={`${id}-dashboard-btn`}
                                                disabled={
                                                    (item as any)?.disabled
                                                }
                                                onClick={() => {
                                                    navigate(item.path);
                                                    setSublist("");
                                                }}
                                            >
                                                <ListItemText>
                                                    <Typography variant="body2">
                                                        {item.title}
                                                    </Typography>
                                                </ListItemText>
                                            </ListItemButton>
                                        </ListItem>
                                    ),
                            )}
                        </List>
                    </Paper>
                </div>
            </div>
        </ClickAwayListener>
    );
};

export default React.memo(Sidebar);
